import React, { useCallback, useEffect } from 'react';

import { Button, StandardButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { InfoCard } from '@eika/card';
import { Icon, IconTypes } from '@eika/icon';

import { useConfig } from '../../../defaultConfig';
import { avtalegiroSelector, useAccounts } from '../../../fetchHooks/useAccount';
import type { Account, ApprovalStatus } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { isValidAccountPaymentFrom } from '../../../services/accountFilters';
import SigningContextFlow from '../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../signingContext/signingContext';
import { TransactionForm, TransactionSubmitWrapper } from '../../TransactionForm';
import { ErrorCardWrapper } from '../../shared/ErrorCardWrapper';
import AccountsEmptyInfo from '../AccountsEmptyInfo';
import AvtaleGiroResultInfo from '../AvtaleGiroResultInfo';
import AvtaleGiroRetry from '../AvtaleGiroRetry';
import { AvtaleGiroFields } from './fields';
import { AvtaleGiroActionType } from './form/actions';
import createAvtalegiroRequest from './form/createAvtalegiroRequest';
import useFormContext from './form/useFormContext';

import './CreateAvtaleGiroForm.scss';

const hasSigningError = (paymentStatus?: ApprovalStatus) => {
    return paymentStatus === 'REJECTED' || paymentStatus === 'UNKNOWN';
};

interface Props {
    onClose: () => void;
    account?: Account;
    title?: string;
    lockKid?: boolean;
    lockReceiver?: boolean;
    isInModal?: boolean;
}

const CreateAvtaleGiroForm: React.FC<Props> = ({
    onClose,
    account,
    title,
    lockKid,
    lockReceiver,
    isInModal = true,
}) => {
    const translated = useTranslation();
    const {
        featureToggles: { disableAvtaleGiroChanges },
    } = useConfig();

    const { state, handleChange, checkFormValidity } = useFormContext();
    const { data: accounts, ...accountState } = useAccounts({ select: avtalegiroSelector });
    const {
        signingStatus,
        signingError,
        signingOperationPending,
        handleCancelSigningFlow,
        startSigning,
        showPaymentRetry,
    } = useSigningContext(createAvtalegiroRequest());

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) {
                startSigning(state);
            }
        },
        [startSigning, checkFormValidity, state],
    );

    useEffect(() => {
        if (account && !state.fromAccount) {
            if (isValidAccountPaymentFrom(account)) {
                handleChange({ type: AvtaleGiroActionType.FROM_ACCOUNT_SELECTED, payload: account.accountNumber });
            }
        }
    }, [account, handleChange, state.fromAccount]);

    const animateModalAs = isInModal ? undefined : 'sub';
    if (disableAvtaleGiroChanges) {
        return (
            <InfoCard className="create-avtalegiro-info-card" skipBackground>
                <Icon className="create-avtalegiro-info-card__icon" type={IconTypes.UTROPSTEGN_36} />
                <InfoCard.TitleH2>{translated.disableAvtaleGiroChanges.creationUnavailableTitle}</InfoCard.TitleH2>
                <p>{translated.disableAvtaleGiroChanges.creationUnavailableDescription}</p>
                <StandardButton className="create-avtalegiro-info-card__btn" onClick={onClose}>
                    {translated.ok}
                </StandardButton>
            </InfoCard>
        );
    }

    if (showPaymentRetry) {
        return <AvtaleGiroRetry onClose={onClose} />;
    }

    if (signingError || signingStatus === 'COMPLETED' || hasSigningError(signingStatus)) {
        return (
            <AvtaleGiroResultInfo
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                onClose={onClose}
                onRetryManually={() => handleCancelSigningFlow()}
            />
        );
    }

    if (accountState.error) {
        return (
            <ErrorCardWrapper
                defaultError={translated.errors.avtaleGiroErrorFetchingAccounts}
                exception={accountState.error}
                isModal
            >
                <Button onClick={() => accountState.refetch()}>{translated.tryAgain}</Button>
            </ErrorCardWrapper>
        );
    }

    if (accountState.isFetched && !accounts!.length) {
        return <AccountsEmptyInfo title={translated.accountsEmptyInfo.paymentAgreementTitle} />;
    }

    return (
        <SigningContextFlow onClosePayment={onClose}>
            <h2 className="avtalegiro-form__title">{title ?? translated.avtaleGiroAgreement.createTitle}</h2>
            <TransactionForm onSubmit={onSubmit} className="avtalegiro__transaction-form" doubleAmountFields>
                <AvtaleGiroFields lockKid={lockKid} lockReceiver={lockReceiver} animateModalAs={animateModalAs} />
                <TransactionSubmitWrapper className="avtalegiro-form__submit-container">
                    <ButtonWithSpinner
                        className="avtalegiro-form__submit"
                        buttonType="positive"
                        type="submit"
                        data-testid="create-button"
                        showSpinner={signingOperationPending}
                    >
                        {translated.avtaleGiroAgreement.createDescription}
                    </ButtonWithSpinner>
                </TransactionSubmitWrapper>
            </TransactionForm>
        </SigningContextFlow>
    );
};

export default CreateAvtaleGiroForm;
